import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { cn } from '../lib/utils';
import { detectPlatform, n8nService } from '../lib/n8nService';
import { Crosshair, Link, Loader2, AlertCircle } from 'lucide-react';
import { PlatformBadge } from './PlatformBadge';

export function SnipeModal({ open, onOpenChange, onSnipe }) {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState('');

  const { t } = useTranslation();
  const detectedPlatform = detectPlatform(url);
  const isConfigured = n8nService.isConfigured();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!url) {
      setError('Please enter a product URL');
      return;
    }

    if (!language) {
      setError('Please select an output language');
      return;
    }

    // Validate URL format
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL (e.g., https://amazon.com/dp/...)');
      return;
    }

    // Check if n8n is configured
    if (!isConfigured) {
      setError('n8n integration is not configured. Please contact admin.');
      return;
    }

    setIsLoading(true);
    try {
      await onSnipe({
        url: url.trim(),
        platform: detectedPlatform,
        language,
      });
      setUrl('');
      setLanguage('');
      onOpenChange(false);
    } catch (err) {
      console.error('Snipe error:', err);
      // Provide user-friendly error messages
      if (err.message?.includes('Network error')) {
        setError('Unable to connect to the server. Please try again.');
      } else if (err.message?.includes('webhook')) {
        setError('Product submission service is unavailable. Please try again later.');
      } else {
        setError(err.message || 'Failed to submit product. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setUrl('');
    setLanguage('');
    setError('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="snipe-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#1E1B4B] font-['Manrope'] flex items-center gap-2">
            <Crosshair className="w-6 h-6 text-cyan-500" />
            {t('snipe.title')}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {t('snipe.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div className="space-y-2">
            <Label htmlFor="url" className="text-sm font-medium text-slate-700">
              {t('snipe.urlLabel')}
            </Label>
            <div className="relative">
              <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="url"
                type="url"
                placeholder="https://amazon.com/dp/..."
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setError('');
                }}
                className={cn(
                  'pl-10 py-3 border-2',
                  error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-slate-200 focus:border-cyan-500 focus:ring-cyan-500'
                )}
                data-testid="snipe-url-input"
              />
            </div>
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}
            <p className="text-xs text-slate-500">
              {t('snipe.supportedPlatforms')}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">
              {t('snipe.detectedPlatform')}
            </Label>
            {url ? (
              <PlatformBadge platform={detectedPlatform} size="md" />
            ) : (
              <div className="px-4 py-3 rounded-lg border-2 bg-slate-50 border-slate-200 text-slate-500 text-sm">
                Auto-detect from URL
              </div>
            )}
          </div>

          {/* Output Language */}
          <div className="space-y-2">
            <Label htmlFor="language" className="text-sm font-medium text-slate-700">
              {t('snipe.outputLanguage')}
            </Label>
            <select
              id="language"
              value={language}
              onChange={(e) => {
                setLanguage(e.target.value);
                setError('');
              }}
              className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 text-slate-700 bg-white focus:border-cyan-500 focus:ring-cyan-500 text-sm"
            >
              <option value="">{t('language.select')}</option>
              <option value="English">{t('language.english')}</option>
              <option value="French">{t('language.french')}</option>
              <option value="Italian">{t('language.italian')}</option>
              <option value="German">{t('language.german')}</option>
            </select>
            <p className="text-xs text-slate-500">
              {t('snipe.outputLanguageHelp')}
            </p>
          </div>

          {/* Info box */}
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
            <p className="text-sm text-cyan-800">
              <strong>What happens next?</strong> Once submitted, the product will be automatically analyzed for:
            </p>
            <ul className="text-sm text-cyan-700 mt-2 space-y-1 list-disc list-inside">
              <li>Product details & images</li>
              <li>Review analysis (positive/negative)</li>
              <li>Marketing angles & competitive edge</li>
            </ul>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className={cn(
                'flex-1 px-5 py-3 border-2 border-slate-300 rounded-lg',
                'text-slate-700 font-semibold hover:bg-slate-50 transition-colors'
              )}
              data-testid="snipe-cancel-btn"
            >
              {t('snipe.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || !url}
              className={cn(
                'flex-1 px-5 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg',
                'font-semibold transition-colors shadow-lg shadow-cyan-500/30',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'flex items-center justify-center gap-2'
              )}
              data-testid="snipe-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('snipe.submitting')}
                </>
              ) : (
                <>
                  <Crosshair className="w-4 h-4" />
                  {t('snipe.submit')}
                </>
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
